'use client'

import { useState } from 'react'

import AppImage from '../AppImage'

import dynamic from 'next/dynamic'
const LightboxComponent = dynamic(() => import('@/components/lightbox/Lightbox'))

type ImageProps = {
  image: {
    url: string
    height: number
    width: number
    alt?: string
    caption?: string
  }
  reduceSize?: boolean
  lazy?: boolean
}

export default function CustomImage({
  image: { url: src, height, width, alt = '', caption },
  reduceSize = false,
  lazy = true,
}: ImageProps) {
  if (!src) {
    return null
  }

  const [lightboxOpen, setLightboxOpen] = useState(false)

  const transforms = {
    height: '1680',
    width: '1680',
    focus: 'center', // disables auto cropping
    crop: 'at_max',
  }

  if (!src) {
    return null
  }

  return (
    <div className="component component--image">
      <div className="container">
        <div className="flex justify-center">
          <figure
            className={`mx-auto inline-flex w-full cursor-zoom-in flex-col ${!width ? 'w-full items-center' : ''} ${reduceSize ? '@s-up/main:w-auto' : 'w-full'}`}
            style={
              width
                ? {
                    maxWidth: `${width}px`,
                  }
                : {}
            }
            onClick={() => setLightboxOpen(true)}>
            <AppImage
              path={src}
              alt={alt}
              height={height}
              width={width}
              loading="lazy"
              // Default transform
              transforms={transforms}
              style={{
                aspectRatio: `${width}/${height}`,
              }}
              className={`h-auto w-auto rounded-md ${reduceSize ? 'max-h-[max(80vh,340px)] max-w-[var(--container-s-max-w)]' : 'w-full'}`}
              // Describes the size of the image in the layout
              sizes="min(1720px, calc(100vw - 40px)"
              // Srcset transforms
              srcSet={{
                260: { width: '260', height: '260' },
                651: { width: '651', height: '651' },
                960: { width: '960', height: '960' },
                1390: { width: '1390', height: '1390' },
                1680: { width: '1680', height: '1680' },
                1720: { width: '1720', height: '1720' },
                1890: { width: '1890', height: '1890' },
                2048: { width: '2048', height: '2048' },
              }}
            />

            {caption && (
              <figcaption
                className="text-body mt-s inline-flex"
                dangerouslySetInnerHTML={{ __html: caption }}></figcaption>
            )}
          </figure>
        </div>

        {lightboxOpen ? (
          <LightboxComponent
            index={0}
            slides={[
              {
                src: src,
                alt: alt,
                height: height,
                width: width,
              },
            ]}
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            carousel={{ finite: true }} // Disables infinite looping
            animation={{
              fade: 400,
              swipe: 400,
              navigation: 0,
            }}
            plugins={['zoom']}
          />
        ) : null}
      </div>
    </div>
  )
}
